import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface SidebarItem {
  icon: ReactNode;
  label?: string;
  href: string;
  active: boolean;
}

interface SidebarProps {
  items: SidebarItem[];
  isMobile: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ items, isMobile }) => {
  if (isMobile) {
    // Mobile: full-width dropdown panel, height fits content, icon + label always shown.
    return (
      <div className="self-stretch flex flex-col justify-start items-stretch gap-1 p-2">
        {items.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className={`self-stretch h-10 rounded-md flex items-center gap-2 px-2 whitespace-nowrap outline outline-1 outline-offset-[-1px] ${
              item.active
                ? 'bg-white-alpha-20 outline-white-alpha-20'
                : 'outline-transparent hover:bg-white-alpha-10 hover:outline-white-alpha-10'
            }`}
          >
            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-white-alpha-80">
              {item.icon}
            </div>
            {item.label && (
              <span className="text-white-alpha-90 typo-label">{item.label}</span>
            )}
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className={`relative self-stretch ${isMobile ? 'block md:hidden' : 'hidden md:block'}`}>
      {/* Spacer to reserve w-12 in the layout flow */}
      <div className="w-12 self-stretch" />
      {/* Actual sidebar: absolute so hover expansion overlays content */}
      <div className="group absolute inset-y-0 left-0 z-10 w-48 md:w-12 hover:w-48 pt-1 pb-8 bg-primary-500 flex flex-col justify-start items-start gap-4 overflow-hidden transition-[width] duration-200 ease-in-out">
        <div className="self-stretch flex-1 flex flex-col justify-start items-center gap-1">
          {items.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`self-stretch mx-2 h-8 rounded-md flex items-center gap-2 px-1.5 whitespace-nowrap outline outline-1 outline-offset-[-1px] ${
                item.active
                  ? 'bg-white-alpha-20 outline-white-alpha-20'
                  : 'hover:bg-white-alpha-10 outline-transparent hover:outline-white-alpha-10'
              }`}
            >
              <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-white-alpha-80">
                {item.icon}
              </div>
              {item.label && (
                <span className="text-white-alpha-90 typo-label opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200">
                  {item.label}
                </span>
              )}
            </Link>
          ))}
        </div>
        <div className="self-stretch h-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-primary-400" style={{ zIndex: -1 }} />
      </div>
    </div>
  );
};

export default Sidebar;
